import { createSelector } from '@reduxjs/toolkit';
import { overquotaNonPromoFlow } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNonPromoFlow';
import { getIntegrationClient } from 'reactApp/modules/integration/integration.selectors';
import { isOpenPopups } from 'reactApp/modules/popup/popup.selectors';
import { type EPromoType, type IPromoState, isNeedsPromo } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { RootState } from 'reactApp/store';

const getPromoRoot = (state: RootState) => state.promo as IPromoState;

export const getPromoQueue = createSelector(getPromoRoot, (state) => [...state.queue]);
export const getCurrentPromo = createSelector(getPromoRoot, (state) => state.currentPromo);
export const getLastPromoShowTime = createSelector(getPromoRoot, (state) => state.lastPromoShownTimestamp);
export const get3PromoShown = createSelector(getPromoRoot, (state) => state.threePromoShown);

export const getPromo = <T extends EPromoType>(promoType: T) =>
    createSelector(getPromoRoot, (state) => (isNeedsPromo(promoType, state.currentPromo) ? state.currentPromo : undefined));

export const pullPromo = <T extends EPromoType>(promoType: T) =>
    createSelector(getPromoRoot, getPromo(promoType), (state, getPromo) => {
        if (!state.currentPromo) {
            const find = state.queue.find((item) => item.type === promoType);
            return isNeedsPromo(promoType, find) ? find : getPromo;
        }

        return getPromo;
    });

/**
 * Промки могут встать в очередь, но не показываем ничего, пока не принято ЛС(не считая пабликов) или если пользователь заморожен
 * или если мы в инлайновой интеграции или если интеграция и интеграционный клиент - тутория
 */
export const isPromoCanBeShown = createSelector(
    UserSelectors.isNewbie,
    UserSelectors.isFrozen,
    getCurrentStorage,
    getIntegrationClient,
    UserQuotaSelectors.isOverquotaBySpaceAndApi,
    isOpenPopups,
    (isNewbie, isFrozen, storage, integrationClient, isOverquota, isAnyPopupVisible) => {
        const { isPublic, isInlineIntegration, isIntegration } = getStorage(storage);
        const { isTutoria } = integrationClient;

        const res = !(isNewbie && !isPublic) && !isFrozen && !isInlineIntegration && !(isIntegration && isTutoria);

        if (res && overquotaNonPromoFlow) {
            return !(isOverquota && isAnyPopupVisible);
        }

        return res;
    }
);

export const PromoSelectors = {
    getPromoQueue,
    getCurrentPromo,
    getLastPromoShowTime,
    get3PromoShown,
    getPromo,
    pullPromo,
};
